import { GraphQLClient, gql, type ClientError } from 'graphql-request';
import { Query, QueryScoreAssessmentsForFirmArgs } from '../generated/graphql';
import { MonetaryAmountString } from './fragments';

export interface GetScoreAssessmentsOnDemandForFirmArgs extends QueryScoreAssessmentsForFirmArgs {
  includeEstateStatus?: boolean;
}

const ESTATE_STATUS_MISSING_FIELD_MESSAGE = 'Cannot query field "es" on type "ScorecardScores".';

const isEstateStatusMissingFieldValidationError = (error: unknown): boolean => {
  const errors = (error as ClientError | undefined)?.response?.errors;

  return (
    Array.isArray(errors) &&
    errors.length === 1 &&
    errors[0]?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' &&
    errors[0].message.startsWith(ESTATE_STATUS_MISSING_FIELD_MESSAGE)
  );
};

export const getScoreAssessmentsQuery = (includeEstateStatus = false) => gql`
  query ScoreAssessmentsForFirm($firmID: ID!, $cursor: String) {
    scoreAssessmentsForFirm(firmID: $firmID, cursor: $cursor) {
      items {
        firmClient {
          id
          mode
          firmID
          givenNamesDisplay
          familyNamesDisplay
          household {
            id
          }
          aiSummary
          individuals {
            age
            emailAddress
            familyName
            givenName
            profileColor
            profileImage
          }
          group {
            id
            name
          }
        }
        netWorth {${MonetaryAmountString}}
        annualIncome {${MonetaryAmountString}}
        annualDebtPayments {${MonetaryAmountString}}
        annualSavings {${MonetaryAmountString}}
        liquidity {${MonetaryAmountString}}
        taxInfo {
          mostRecentTaxYear
          taxesPaid {${MonetaryAmountString}}
          taxedIncome {${MonetaryAmountString}}
        }
        annualExpenses {${MonetaryAmountString}}
        qualifiedAssets {${MonetaryAmountString}}
        realEstateEquity {${MonetaryAmountString}}
        businessEquity {${MonetaryAmountString}}
        scorecard {
          sr
          br
          dr
          tr
          tt
          lt
          qt
          rt
          bt
          ir
          ${includeEstateStatus ? 'es' : ''}
        }
        lifeCoverages {
          person {
            givenName
          }
          overallCoverage
        }
        disabilityCoverages {
          person {
            givenName
          }
          overallCoverage
        }
        liabilityCoverages {
          person {
            givenName
          }
          overallCoverage
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const MAX_SCORE_ASSESSMENT_PAGES = 200;

export const clientGetScoreAssessmentsOnDemandForFirm = async (
  client: GraphQLClient,
  args: GetScoreAssessmentsOnDemandForFirmArgs
) => {
  const { includeEstateStatus, ...queryArgs } = args;
  let includeEstateStatusForRequest = includeEstateStatus === true;

  const requestPage = async (pageArgs: QueryScoreAssessmentsForFirmArgs) => {
    try {
      return (
        await client.request<Pick<Query, 'scoreAssessmentsForFirm'>>(
          getScoreAssessmentsQuery(includeEstateStatusForRequest),
          pageArgs
        )
      ).scoreAssessmentsForFirm;
    } catch (error) {
      if (!includeEstateStatusForRequest || !isEstateStatusMissingFieldValidationError(error)) {
        throw error;
      }

      includeEstateStatusForRequest = false;
      return (await client.request<Pick<Query, 'scoreAssessmentsForFirm'>>(getScoreAssessmentsQuery(false), pageArgs))
        .scoreAssessmentsForFirm;
    }
  };

  const result = await requestPage(queryArgs);
  const seenClientIDs = new Set<string>();
  result.items = result.items.filter((item) => {
    const clientID = item.firmClient?.id;
    if (!clientID) return true;
    if (seenClientIDs.has(clientID)) return false;
    seenClientIDs.add(clientID);
    return true;
  });

  const seenCursors = new Set<string>(queryArgs.cursor ? [queryArgs.cursor] : []);
  let pageCount = 1;

  while (result.pageInfo.hasNextPage) {
    const cursor = result.pageInfo.endCursor;
    if (!cursor) {
      throw new Error('Score assessment pagination is missing an end cursor.');
    }
    if (seenCursors.has(cursor)) {
      throw new Error('Score assessment pagination repeated an end cursor.');
    }
    if (pageCount >= MAX_SCORE_ASSESSMENT_PAGES) {
      throw new Error('Score assessment pagination exceeded the maximum page count.');
    }

    seenCursors.add(cursor);
    pageCount += 1;
    const nextResult = await requestPage({ ...queryArgs, cursor });
    for (const item of nextResult.items) {
      const clientID = item.firmClient?.id;
      if (clientID && seenClientIDs.has(clientID)) continue;
      if (clientID) seenClientIDs.add(clientID);
      result.items.push(item);
    }
    result.pageInfo = nextResult.pageInfo;
  }

  return result;
};
